import { useEffect, useRef } from "react";

// MUI
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Skeleton,
  Button,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Context
import { useDashboard } from "context/DashboardContext";

// Helpers
import EmptyData from "../../helpers/EmptyData";

const extractFileName = (path = "") => path.split("/").pop() || "Unknown file";

function MerchantDocuments({ merchantId, merchantData }) {
  const {
    fetchAllMerchantDocuments,
    merchantDocuments,
    loadingMerchantDocs,
    merchantDocError,
    confirmDocument,
  } = useDashboard();

  // ✅ prevent refetching same merchant
  const lastFetchedId = useRef(null);

  useEffect(() => {
    if (!merchantId) return;

    if (lastFetchedId.current === merchantId) return;

    lastFetchedId.current = merchantId;
    fetchAllMerchantDocuments(merchantId);
  }, [merchantId, fetchAllMerchantDocuments]);

  // console.log("merchantId check", typeof(merchantId));

  return (
    <SoftBox py={3}>
      <Card>
        {/* HEADER */}
        <SoftBox p={3}>
          {/* <SoftTypography variant='h6'>Merchant Documents</SoftTypography> */}
          {/* <SoftTypography variant='caption' color='text'>
            Merchant ID: {merchantId}
          </SoftTypography> */}
          <SoftTypography variant='body2'>
            Merchant's Business Type is{" "}
            {merchantData?.merchantExt?.businessType || "N/A"}
          </SoftTypography>
        </SoftBox>

        {/* ERROR */}
        {merchantDocError && (
          <Alert severity='error' sx={{ mx: 3 }}>
            Failed to fetch merchant documents
          </Alert>
        )}

        {/* LOADING */}
        {loadingMerchantDocs && (
          <SoftBox p={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant='rectangular'
                height={40}
                sx={{ mb: 1, borderRadius: "8px" }}
              />
            ))}
          </SoftBox>
        )}

        {/* EMPTY */}
        {!loadingMerchantDocs &&
          Array.isArray(merchantDocuments) &&
          merchantDocuments.length === 0 && (
            <EmptyData message='No documents uploaded for this merchant.' />
          )}

        {/* TABLE */}
        {!loadingMerchantDocs &&
          Array.isArray(merchantDocuments) &&
          merchantDocuments.length > 0 && (
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Document Type</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align='center'>Preview</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {merchantDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        {doc.documentType.replaceAll("_", " ")}
                      </TableCell>

                      <TableCell>{extractFileName(doc.fileName)}</TableCell>

                      <TableCell>
                        {doc.verificationStatus ? "Verified" : "Pending"}
                      </TableCell>

                      <TableCell align='center'>
                        <IconButton
                          onClick={() => window.open(doc.downloadUrl, "_blank")}
                          color='primary'
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>

                      <TableCell align='center'>
                        <Button
                          variant='outlined'
                          size='small'
                          disabled={doc.verificationStatus}
                          onClick={() =>
                            confirmDocument({
                              merchantId,
                              fileId: doc.id,
                            })
                          }
                          sx={{
                            height: 30,
                            borderRadius: "8px",
                            color: "darkblue",
                          }}
                        >
                          {doc.verificationStatus ? "Verified" : "Confirm File"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Card>
    </SoftBox>
  );
}

export default MerchantDocuments;
